import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FiX } from "react-icons/fi";
import Button from "../../../../../components/ui/Button";
import InputField from "../../../../../components/ui/InputField";
import { useTheme } from "../../../../../context/ThemeContext";
import { Zone, ZoneFormData } from "../../../../../types/zone";

interface ZoneFormModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (data: ZoneFormData) => Promise<void>;
    initialData?: Zone | null;
    isLoading?: boolean;
}

const ZoneFormModal: React.FC<ZoneFormModalProps> = ({
    isOpen,
    onClose,
    onSubmit,
    initialData,
    isLoading = false,
}) => {
    const { t } = useTranslation();
    const { theme } = useTheme();
    const [formData, setFormData] = useState<ZoneFormData>({
        zone_name: "",
        ar_zone_name: "",
        fee: 0,
    });
    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    useEffect(() => {
        if (isOpen) {
            if (initialData) {
                setFormData({
                    zone_name: initialData.zone_name,
                    ar_zone_name: initialData.ar_zone_name,
                    fee: initialData.fee,
                });
            } else {
                setFormData({
                    zone_name: "",
                    ar_zone_name: "",
                    fee: 0,
                });
            }
            setErrors({});
        }
    }, [isOpen, initialData]);

    const validate = () => {
        const newErrors: { [key: string]: string } = {};

        if (!formData.zone_name.trim()) {
            newErrors.zone_name = t("menuManagement.zones.zoneNameRequired");
        }

        if (!formData.ar_zone_name.trim()) {
            newErrors.ar_zone_name = t("menuManagement.zones.arZoneNameRequired");
        }

        if (formData.fee < 0) {
            newErrors.fee = t("menuManagement.zones.feeInvalid");
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validate()) {
            return;
        }

        try {
            await onSubmit(formData);
            onClose();
        } catch (error) {
            // Error handled by parent
        }
    };

    const handleChange = (field: keyof ZoneFormData, value: string | number) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
        if (errors[field]) {
            setErrors((prev) => ({
                ...prev,
                [field]: "",
            }));
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black/80 transition-opacity"
                onClick={onClose}
            />

            {/* Modal */}
            <div className="flex min-h-full items-center justify-center p-4">
                <div
                    className={`relative w-full max-w-md transform overflow-hidden rounded-lg shadow-xl transition-all ${theme === "dark" ? "bg-gray-800" : "bg-white"
                        }`}
                >
                    {/* Header */}
                    <div
                        className={`flex items-center justify-between border-b px-6 py-4 ${theme === "dark" ? "border-gray-700" : "border-gray-200"
                            }`}
                    >
                        <h3
                            className={`text-lg font-semibold ${theme === "dark" ? "text-white" : "text-gray-900"
                                }`}
                        >
                            {initialData
                                ? t("menuManagement.zones.editZone")
                                : t("menuManagement.zones.addZone")}
                        </h3>
                        <button
                            onClick={onClose}
                            className={`rounded-lg p-1 transition-colors ${theme === "dark"
                                ? "hover:bg-gray-700 text-gray-400"
                                : "hover:bg-gray-100 text-gray-500"
                                }`}
                        >
                            <FiX className="h-5 w-5" />
                        </button>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="px-6 py-4">
                        <div className="space-y-4">
                            {/* Zone Name (English) */}
                            <div>
                                <label
                                    htmlFor="zone_name"
                                    className={`mb-1 block text-sm font-medium ${theme === "dark" ? "text-gray-200" : "text-gray-700"
                                        }`}
                                >
                                    {t("menuManagement.zones.zoneName")}
                                </label>
                                <InputField
                                    id="zone_name"
                                    type="text"
                                    value={formData.zone_name}
                                    onChange={(e) => handleChange("zone_name", e.target.value)}
                                    placeholder={t("menuManagement.zones.zoneNamePlaceholder")}
                                    error={errors.zone_name}
                                    className="w-full"
                                />
                            </div>

                            {/* Zone Name (Arabic) */}
                            <div>
                                <label
                                    htmlFor="ar_zone_name"
                                    className={`mb-1 block text-sm font-medium ${theme === "dark" ? "text-gray-200" : "text-gray-700"
                                        }`}
                                >
                                    {t("menuManagement.zones.arZoneName")}
                                </label>
                                <InputField
                                    id="ar_zone_name"
                                    type="text"
                                    value={formData.ar_zone_name}
                                    onChange={(e) => handleChange("ar_zone_name", e.target.value)}
                                    placeholder={t("menuManagement.zones.arZoneNamePlaceholder")}
                                    error={errors.ar_zone_name}
                                    className="w-full"
                                    dir="rtl"
                                />
                            </div>

                            {/* Delivery Fee */}
                            <div>
                                <label
                                    htmlFor="fee"
                                    className={`mb-1 block text-sm font-medium ${theme === "dark" ? "text-gray-200" : "text-gray-700"
                                        }`}
                                >
                                    {t("menuManagement.zones.fee")}
                                </label>
                                <InputField
                                    id="fee"
                                    type="number"
                                    value={formData.fee.toString()}
                                    onChange={(e) => handleChange("fee", parseFloat(e.target.value) || 0)}
                                    placeholder="0.00"
                                    error={errors.fee}
                                    className="w-full"
                                    min="0"
                                    step="0.01"
                                />
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="mt-6 flex justify-end gap-3">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={onClose}
                                disabled={isLoading}
                            >
                                {t("common.cancel")}
                            </Button>
                            <Button
                                type="submit"
                                className="bg-primary hover:bg-primary/90"
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <div className="flex items-center gap-2">
                                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                                        {t("common.saving")}
                                    </div>
                                ) : (
                                    t("common.save")
                                )}
                            </Button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default ZoneFormModal;
